<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch, type ComputedRef, type Ref } from "vue";
import {
  loadProbeInternetIncidents,
  loadTenantInternetAvailability,
  type InternetAvailabilityBucketDuration,
  type InternetAvailabilityBucket,
  type InternetAvailabilityBucketStatus,
  type InternetAvailabilitySummaryPeriod,
  type InternetAvailabilityWindowSummary,
  type InternetIncident,
  type ProbeInternetAvailability,
  type ProbeInternetIncidentLog,
  type TenantInternetAvailability,
} from "../api/measurements.js";
import { listTenantProbes, type ProbeInventoryItem } from "../api/probes.js";
import type { TenantSummary } from "../api/tenants.js";
import AppShell from "../components/AppShell.vue";
import DateTimePicker from "../components/DateTimePicker.vue";
import { formatDateTime } from "../formatting/date-time.js";
import { clampTooltipPosition, horizontalTooltipBounds } from "../components/tooltip-position.js";
import { useTenantStore } from "../stores/tenants.js";
import { useUserSettingsStore } from "../stores/user-settings.js";

const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore();
const userSettingsStore: ReturnType<typeof useUserSettingsStore> = useUserSettingsStore();
const tenant: ComputedRef<TenantSummary | null> = computed(
  (): TenantSummary | null => tenantStore.selectedTenant,
);
const probes: Ref<ProbeInventoryItem[]> = ref([]);
const probesLoadingError: Ref<string | null> = ref(null);
const availability: Ref<TenantInternetAvailability | null> = ref(null);
const availabilityLoadingError: Ref<string | null> = ref(null);
const availabilityLoading: Ref<boolean> = ref(false);
const availabilityLoadingIndicatorVisible: Ref<boolean> = ref(false);
const selectedBucketDuration: Ref<InternetAvailabilityBucketDuration> = ref(userSettingsStore.settings.dashboardDefaultResolution);
const selectedRangeFrom: Ref<string> = ref(new Date(Date.now() - 24 * 3_600_000).toISOString());
const selectedRangeTo: Ref<string> = ref(new Date().toISOString());
type AvailabilityRangePreset = "live" | "today" | "this_week" | "this_month" | "last_month" | "this_year" | "last_year" | "custom";
const selectedRangePreset: Ref<AvailabilityRangePreset> = ref(userSettingsStore.settings.dashboardDefaultPreset);
const availabilityCursor: Ref<string | null> = ref(null);
const availabilityCursorHistory: Ref<string[]> = ref([]);
const expandedProbeIds: Ref<Set<string>> = ref(new Set<string>());
const detailProbeId: Ref<string | null> = ref(null);
const incidentLog: Ref<ProbeInternetIncidentLog | null> = ref(null);
const incidentLoadingError: Ref<string | null> = ref(null);
const incidentsLoading: Ref<boolean> = ref(false);
const bucketTooltipElement: Ref<HTMLElement | null> = ref(null);
const bucketTooltipPanel: Ref<{
  readonly visible: boolean;
  readonly text: string;
  readonly left: number;
  readonly top: number;
  readonly arrowLeft: number;
}> = ref({
  visible: false,
  text: "",
  left: 0,
  top: 0,
  arrowLeft: 0,
});

const bucketTooltipGapPx: number = 14;
const bucketTooltipViewportMarginPx: number = 12;
const dashboardPollingIntervalMs: number = 60_000;
const availabilityLoadingIndicatorDelayMs: number = 3_000;
let dashboardPollingIntervalId: number | null = null;
let dashboardRefreshInProgress: boolean = false;
let availabilityLoadingIndicatorTimeoutId: number | null = null;

const overdueProbeCount: ComputedRef<number> = computed(
  (): number => probes.value.filter((probe: ProbeInventoryItem): boolean => probe.overdueMs > 0).length,
);
const rangePickerDisabled: ComputedRef<boolean> = computed((): boolean => selectedRangePreset.value !== "custom");

function resolutionMilliseconds(value: string): number | null {
  const match: RegExpExecArray | null = /^([1-9][0-9]*)(m|h|d)$/.exec(value);
  if (match === null) return null;
  const factor: number = match[2] === "m" ? 60_000 : match[2] === "h" ? 3_600_000 : 86_400_000;
  const milliseconds: number = Number(match[1]) * factor;
  return Number.isSafeInteger(milliseconds) ? milliseconds : null;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function updateRangeForPreset(preset: AvailabilityRangePreset): void {
  const now: Date = new Date();
  let from: Date = startOfLocalDay(now);
  let to: Date = now;
  if (preset === "live") {
    const durationMs: number | null = resolutionMilliseconds(selectedBucketDuration.value);
    if (durationMs === null) return;
    from = new Date(now.getTime() - 120 * durationMs);
  } else if (preset === "this_week") {
    const daysSinceMonday: number = (now.getDay() + 6) % 7;
    from.setDate(from.getDate() - daysSinceMonday);
  } else if (preset === "this_month") {
    from = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (preset === "last_month") {
    from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    to = new Date(now.getFullYear(), now.getMonth(), 1);
  } else if (preset === "this_year") {
    from = new Date(now.getFullYear(), 0, 1);
  } else if (preset === "last_year") {
    from = new Date(now.getFullYear() - 1, 0, 1);
    to = new Date(now.getFullYear(), 0, 1);
  }
  selectedRangeFrom.value = from.toISOString();
  selectedRangeTo.value = to.toISOString();
}

function formatRole(role: string | null): string {
  if (role === null) {
    return "System Admin";
  }
  return role
    .split("_")
    .map((part: string): string => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

async function loadProbes(): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || !currentTenant.access.capabilities.includes("probe.read")) {
    probes.value = [];
    return;
  }
  probesLoadingError.value = null;
  try {
    probes.value = [...await listTenantProbes(currentTenant.id)];
  } catch (error: unknown) {
    probes.value = [];
    probesLoadingError.value = error instanceof Error ? error.message : "Probe inventory unavailable";
  }
}

async function loadAvailability(probeId: string | null = null, includeBuckets: boolean = false): Promise<void> {
  if (availabilityLoading.value) {
    return;
  }
  const currentTenant: TenantSummary | null = tenant.value;
  if (currentTenant === null || !currentTenant.access.capabilities.includes("measurement.read")) {
    availability.value = null;
    return;
  }
  availabilityLoadingError.value = null;
  availabilityLoading.value = true;
  if (availability.value !== null) {
    availabilityLoadingIndicatorTimeoutId = window.setTimeout((): void => {
      if (availabilityLoading.value) {
        availabilityLoadingIndicatorVisible.value = true;
      }
    }, availabilityLoadingIndicatorDelayMs);
  }
  try {
    if (selectedRangePreset.value !== "custom") {
      updateRangeForPreset(selectedRangePreset.value);
    }
    if (selectedRangePreset.value === "live") {
      availabilityCursor.value = null;
      availabilityCursorHistory.value = [];
    }
    const result: TenantInternetAvailability = await loadTenantInternetAvailability(
      currentTenant.id,
      selectedRangeFrom.value,
      selectedRangeTo.value,
      selectedBucketDuration.value,
      availabilityCursor.value,
      probeId,
      includeBuckets,
    );
    if (probeId === null || availability.value === null) {
      const previousAvailability: TenantInternetAvailability | null = availability.value;
      availability.value = previousAvailability === null ? result : {
        ...result,
        probes: result.probes.map((probe: ProbeInternetAvailability): ProbeInternetAvailability => {
          const previousProbe: ProbeInternetAvailability | undefined = previousAvailability.probes.find(
            (candidate: ProbeInternetAvailability): boolean => candidate.probeId === probe.probeId,
          );
          return expandedProbeIds.value.has(probe.probeId) && previousProbe !== undefined
            ? { ...probe, buckets: previousProbe.buckets }
            : probe;
        }),
      };
      if (probeId === null) {
        availabilityCursor.value = result.pageFrom === result.from ? null : result.pageFrom;
      }
    } else {
      const fetchedProbe: ProbeInternetAvailability | undefined = result.probes[0];
      if (fetchedProbe !== undefined) {
        availability.value = {
          ...availability.value,
          probes: availability.value.probes.map((probe: ProbeInternetAvailability): ProbeInternetAvailability =>
            probe.probeId === probeId ? fetchedProbe : probe,
          ),
        };
      }
    }
  } catch (error: unknown) {
    availabilityLoadingError.value = error instanceof Error ? error.message : "Internet availability unavailable";
  } finally {
    availabilityLoading.value = false;
    if (availabilityLoadingIndicatorTimeoutId !== null) {
      window.clearTimeout(availabilityLoadingIndicatorTimeoutId);
      availabilityLoadingIndicatorTimeoutId = null;
    }
    availabilityLoadingIndicatorVisible.value = false;
  }
}

async function loadDashboardData(): Promise<void> {
  await Promise.all([loadProbes(), loadAvailability()]);
  await loadExpandedProbeBuckets();
}

async function loadExpandedProbeBuckets(): Promise<void> {
  const probeIds: readonly string[] = [...expandedProbeIds.value];
  for (const probeId of probeIds) {
    if (expandedProbeIds.value.has(probeId)) {
      await loadAvailability(probeId, true);
    }
  }
}

async function refreshDashboardData(): Promise<void> {
  if (dashboardRefreshInProgress) {
    return;
  }
  dashboardRefreshInProgress = true;
  try {
    await loadDashboardData();
  } finally {
    dashboardRefreshInProgress = false;
  }
}

function stopDashboardPolling(): void {
  if (dashboardPollingIntervalId === null) {
    return;
  }
  window.clearInterval(dashboardPollingIntervalId);
  dashboardPollingIntervalId = null;
}

function startDashboardPolling(): void {
  stopDashboardPolling();
  dashboardPollingIntervalId = window.setInterval((): void => {
    void refreshDashboardData();
  }, dashboardPollingIntervalMs);
}

function isProbeExpanded(probeId: string): boolean {
  return expandedProbeIds.value.has(probeId);
}

async function toggleProbe(probeId: string): Promise<void> {
  const nextExpandedProbeIds: Set<string> = new Set<string>(expandedProbeIds.value);
  if (nextExpandedProbeIds.has(probeId)) {
    nextExpandedProbeIds.delete(probeId);
  } else {
    nextExpandedProbeIds.add(probeId);
  }
  expandedProbeIds.value = nextExpandedProbeIds;
  if (nextExpandedProbeIds.has(probeId)) {
    const probe: ProbeInternetAvailability | undefined = availability.value?.probes.find(
      (candidate: ProbeInternetAvailability): boolean => candidate.probeId === probeId,
    );
    if (probe !== undefined && probe.buckets.length === 0) {
      await loadAvailability(probeId, true);
    }
  }
}

function summaryFor(
  probe: ProbeInternetAvailability,
  period: InternetAvailabilitySummaryPeriod,
): InternetAvailabilityWindowSummary | null {
  return probe.summaries.find(
    (summary: InternetAvailabilityWindowSummary): boolean => summary.period === period,
  ) ?? null;
}

function formatPercent(value: number | null): string {
  return value === null ? "—" : `${value.toFixed(2)}%`;
}

function formatDuration(durationMs: number): string {
  if (durationMs < 60_000) {
    return `${Math.round(durationMs / 1_000)}s`;
  }
  if (durationMs < 3_600_000) {
    return `${Math.round(durationMs / 60_000)}m`;
  }
  if (durationMs < 86_400_000) {
    return `${Math.round(durationMs / 3_600_000)}h`;
  }
  return `${Math.round(durationMs / 86_400_000)}d`;
}

function resultLabel(status: InternetAvailabilityBucketStatus): string {
  if (status === "connected") return "OK";
  if (status === "degraded") return "Degraded";
  if (status === "interrupted") return "Interrupted";
  return "No data";
}

function bucketClass(bucket: InternetAvailabilityBucket): string {
  const reclassified: boolean = availability.value?.bucketDuration === "1m" && bucket.hasReclassifiedMeasurements;
  return `internet-availability-timeline__bucket internet-availability-timeline__bucket--${bucket.status}${reclassified ? " internet-availability-timeline__bucket--reclassified" : ""}`;
}

function formatBucketDate(value: string): string {
  const date: Date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);
}

function formatBucketTime(value: string): string {
  const date: Date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return new Intl.DateTimeFormat(undefined, { timeStyle: "medium" }).format(date);
}

function bucketTooltip(bucket: InternetAvailabilityBucket): string {
  return [
    `Date: ${formatBucketDate(bucket.startedAt)}`,
    `Time: ${formatBucketTime(bucket.startedAt)} – ${formatBucketTime(bucket.endedAt)}`,
    `Availability: ${formatPercent(bucket.availabilityPercent)}`,
    `Result: ${resultLabel(bucket.status)}`,
    ...(availability.value?.bucketDuration === "1m" && bucket.hasReclassifiedMeasurements ? ["Classification: reclassified measurement"] : []),
  ].join("\n");
}

async function showBucketTooltip(bucket: InternetAvailabilityBucket, event: MouseEvent | FocusEvent): Promise<void> {
  const targetElement: HTMLElement = event.currentTarget as HTMLElement;
  const targetBounds: DOMRect = targetElement.getBoundingClientRect();
  const anchorX: number = event instanceof MouseEvent ? event.clientX : targetBounds.left + targetBounds.width / 2;
  const anchorY: number = event instanceof MouseEvent ? event.clientY : targetBounds.top;

  bucketTooltipPanel.value = {
    visible: true,
    text: bucketTooltip(bucket),
    left: anchorX,
    top: anchorY,
    arrowLeft: anchorX,
  };

  await nextTick();

  const tooltipElement: HTMLElement | null = bucketTooltipElement.value;
  const tooltipWidth: number = tooltipElement?.offsetWidth ?? 320;
  const tooltipHeight: number = tooltipElement?.offsetHeight ?? 150;
  const horizontalBounds: { readonly left: number; readonly right: number } = horizontalTooltipBounds(targetElement, ".internet-availability-timeline", bucketTooltipViewportMarginPx);
  const maximumLeft: number = horizontalBounds.right - tooltipWidth;
  const minimumLeft: number = horizontalBounds.left;
  const preferredLeft: number = anchorX - tooltipWidth / 2;
  const left: number = maximumLeft < minimumLeft
    ? clampTooltipPosition(preferredLeft, bucketTooltipViewportMarginPx, window.innerWidth - tooltipWidth - bucketTooltipViewportMarginPx)
    : clampTooltipPosition(preferredLeft, minimumLeft, maximumLeft);
  const preferredTop: number = anchorY - tooltipHeight - bucketTooltipGapPx;
  const top: number = preferredTop < bucketTooltipViewportMarginPx
    ? clampTooltipPosition(anchorY + bucketTooltipGapPx, bucketTooltipViewportMarginPx, window.innerHeight - tooltipHeight - bucketTooltipViewportMarginPx)
    : preferredTop;
  const arrowLeft: number = clampTooltipPosition(anchorX - left, 10, tooltipWidth - 10);

  bucketTooltipPanel.value = {
    visible: true,
    text: bucketTooltip(bucket),
    left,
    top,
    arrowLeft,
  };
}

function hideBucketTooltip(): void {
  bucketTooltipPanel.value = {
    visible: false,
    text: "",
    left: 0,
    top: 0,
    arrowLeft: 0,
  };
}

async function applyAvailabilitySelection(): Promise<void> {
  closeIncidentDetail();
  availabilityCursor.value = null;
  availabilityCursorHistory.value = [];
  await loadAvailability();
  await loadExpandedProbeBuckets();
}

async function selectAvailabilityRangePreset(): Promise<void> {
  if (selectedRangePreset.value === "custom") {
    return;
  }
  closeIncidentDetail();
  updateRangeForPreset(selectedRangePreset.value);
  availabilityCursor.value = null;
  availabilityCursorHistory.value = [];
  await loadAvailability();
  await loadExpandedProbeBuckets();
}

async function showNextAvailabilityPage(): Promise<void> {
  if (availability.value?.nextCursor === null || availability.value === null) {
    return;
  }
  availabilityCursorHistory.value = [...availabilityCursorHistory.value, availabilityCursor.value ?? availability.value.from];
  availabilityCursor.value = availability.value.nextCursor;
  await loadAvailability();
  await loadExpandedProbeBuckets();
}

async function showPreviousAvailabilityPage(): Promise<void> {
  const previousCursor: string | undefined = availabilityCursorHistory.value.at(-1);
  if (previousCursor === undefined) {
    return;
  }
  availabilityCursorHistory.value = availabilityCursorHistory.value.slice(0, -1);
  availabilityCursor.value = previousCursor === availability.value?.from ? null : previousCursor;
  await loadAvailability();
  await loadExpandedProbeBuckets();
}

async function openIncidentDetail(probe: ProbeInternetAvailability): Promise<void> {
  const currentTenant: TenantSummary | null = tenant.value;
  const currentAvailability: TenantInternetAvailability | null = availability.value;
  if (currentTenant === null || currentAvailability === null) {
    return;
  }
  detailProbeId.value = probe.probeId;
  incidentsLoading.value = true;
  incidentLoadingError.value = null;
  incidentLog.value = null;
  try {
    incidentLog.value = await loadProbeInternetIncidents(
      currentTenant.id,
      probe.probeId,
      currentAvailability.from,
      currentAvailability.to,
      200,
    );
  } catch (error: unknown) {
    incidentLoadingError.value = error instanceof Error ? error.message : "Internet incidents unavailable";
  } finally {
    incidentsLoading.value = false;
  }
}

function closeIncidentDetail(): void {
  detailProbeId.value = null;
  incidentLog.value = null;
  incidentLoadingError.value = null;
  incidentsLoading.value = false;
}

function incidentStateLabel(state: InternetIncident["state"]): string {
  return state.replaceAll("_", " ");
}
function incidentClassificationLabel(classification: InternetIncident["classification"]): string {
  return classification === "expected_provider_reconnect" ? "Expected provider reconnect" : "Unexpected interruption";
}

onMounted((): void => {
  void refreshDashboardData();
  startDashboardPolling();
});
onUnmounted((): void => {
  stopDashboardPolling();
  if (availabilityLoadingIndicatorTimeoutId !== null) {
    window.clearTimeout(availabilityLoadingIndicatorTimeoutId);
  }
});
watch((): string | null => tenant.value?.id ?? null, refreshDashboardData);
</script>

<template>
  <AppShell v-if="tenant">
    <header class="page-heading">
      <div>
        <p class="eyebrow">{{ tenant.slug }}</p>
        <h1>{{ tenant.displayName }}</h1>
        <p>Monitoring and administration for the currently selected tenant.</p>
      </div>
      <span class="status-pill"><span></span>{{ tenant.status }}</span>
    </header>

    <section class="metric-grid metric-grid--four" aria-label="Tenant summary">
      <article class="metric-card">
        <span class="metric-card__label">Access</span>
        <strong>{{ formatRole(tenant.access.role) }}</strong>
        <span>{{ tenant.access.capabilities.length }} effective capabilities</span>
      </article>
      <article class="metric-card">
        <span class="metric-card__label">Probes</span>
        <strong>{{ probesLoadingError === null ? `${probes.length} / ${tenant.tier.maxProbesPerTenant}` : "—" }}</strong>
        <span>{{ probesLoadingError ?? `${overdueProbeCount} overdue` }}</span>
      </article>
      <article class="metric-card">
        <span class="metric-card__label">Tier</span>
        <strong>{{ tenant.tier.name }}</strong>
        <span>
          {{ tenant.tier.status }}
          <span v-if="tenant.tier.legacy" class="legacy-badge">Legacy</span>
        </span>
      </article>
      <article class="metric-card metric-card--accent">
        <span class="metric-card__label">Tenant status</span>
        <strong>{{ tenant.status.charAt(0).toUpperCase() + tenant.status.slice(1) }}</strong>
        <span>{{ tenant.id }}</span>
      </article>
    </section>

    <section class="content-panel data-table-panel">
      <div class="content-panel__heading">
        <div>
          <p class="eyebrow">Monitoring</p>
          <h2>Internet availability</h2>
          <p>Availability is based on what each probe measured locally, not on whether upload to LacunaScope succeeded.</p>
        </div>
        <form class="availability-range-form" @submit.prevent="applyAvailabilitySelection">
          <label>
            <span>Preset</span>
            <select v-model="selectedRangePreset" @change="selectAvailabilityRangePreset">
              <option value="live">Live</option>
              <option value="today">Today</option>
              <option value="this_week">This week</option>
              <option value="this_month">This month</option>
              <option value="last_month">Last month</option>
              <option value="this_year">This year</option>
              <option value="last_year">Last year</option>
              <option value="custom">Custom</option>
            </select>
          </label>
          <label>
            <span>From</span>
            <DateTimePicker v-model="selectedRangeFrom" :date-time-format="userSettingsStore.settings.dateTimeFormat" ariaLabel="Range start" :disabled="rangePickerDisabled" />
          </label>
          <label>
            <span>To</span>
            <DateTimePicker v-model="selectedRangeTo" :date-time-format="userSettingsStore.settings.dateTimeFormat" ariaLabel="Range end" :disabled="rangePickerDisabled" />
          </label>
          <label>
            <span>Resolution</span>
            <input v-model="selectedBucketDuration" type="text" pattern="[1-9][0-9]*(m|h|d)" title="Positive minutes, hours, or days, for example 1m, 15m, 1h" required>
          </label>
          <button class="button button--ghost" type="submit" :disabled="availabilityLoading">Load</button>
        </form>
      </div>
      <p v-if="availabilityLoadingError" class="form-error" role="alert">{{ availabilityLoadingError }}</p>
      <p v-if="availability === null && availabilityLoading" class="placeholder-panel"><span class="loading-spinner" aria-hidden="true"></span>Loading Internet availability…</p>
      <p v-else-if="availability === null" class="placeholder-panel">Internet availability is unavailable.</p>
      <p v-else-if="availability.probes.length === 0" class="placeholder-panel">No probes are registered for this tenant yet.</p>
      <div v-else class="internet-availability-list">
        <article
          v-for="probe in availability.probes"
          :key="probe.probeId"
          class="internet-availability-row"
        >
          <button
            class="internet-availability-row__summary"
            type="button"
            :aria-expanded="isProbeExpanded(probe.probeId)"
            @click="toggleProbe(probe.probeId)"
          >
            <span class="internet-availability-row__identity">
              <strong>{{ probe.displayName }}</strong>
              <small>{{ isProbeExpanded(probe.probeId) ? "Hide buckets" : "Show buckets" }}</small>
            </span>
            <span class="internet-availability-row__metrics">
              <span>
                <small>24h</small>
                {{ formatPercent(summaryFor(probe, "day")?.availabilityPercent ?? null) }}
              </span>
              <span>
                <small>7d</small>
                {{ formatPercent(summaryFor(probe, "week")?.availabilityPercent ?? null) }}
              </span>
              <span>
                <small>30d</small>
                {{ formatPercent(summaryFor(probe, "month")?.availabilityPercent ?? null) }}
              </span>
              <span>
                <small>
                  Coverage
                  <em>24h window</em>
                </small>
                {{ formatPercent(summaryFor(probe, "day")?.coveragePercent ?? null) }}
              </span>
            </span>
          </button>
          <div v-if="isProbeExpanded(probe.probeId)" class="internet-availability-row__detail">
            <div class="internet-availability-row__detail-heading">
              <span>
                {{ availability.pageSize }} blips · {{ availability.bucketDuration }} per blip ·
                {{ formatDateTime(availability.pageFrom, userSettingsStore.settings.dateTimeFormat) }} –
                {{ formatDateTime(availability.pageTo, userSettingsStore.settings.dateTimeFormat) }}
              </span>
              <button class="button button--ghost" type="button" @click="openIncidentDetail(probe)">Detail view</button>
            </div>
            <div
              class="internet-availability-timeline"
              :aria-label="`Internet availability buckets for ${probe.displayName}`"
              :style="{ '--internet-availability-bucket-count': String(availability.pageSize) }"
            >
              <span
                v-for="bucket in probe.buckets"
                :key="bucket.startedAt"
                :class="bucketClass(bucket)"
                :aria-label="bucketTooltip(bucket)"
                tabindex="0"
                @mouseenter="showBucketTooltip(bucket, $event)"
                @mousemove="showBucketTooltip(bucket, $event)"
                @focus="showBucketTooltip(bucket, $event)"
                @mouseleave="hideBucketTooltip"
                @blur="hideBucketTooltip"
              ></span>
              <span v-if="availabilityLoadingIndicatorVisible" class="internet-availability-timeline__loading-overlay" aria-live="polite"><i class="loading-spinner" aria-hidden="true"></i>Updating availability…</span>
            </div>
            <div class="internet-availability-legend">
              <span><i class="legend-dot legend-dot--connected"></i>Connected</span>
              <span><i class="legend-dot legend-dot--degraded"></i>Degraded</span>
              <span><i class="legend-dot legend-dot--interrupted"></i>Interrupted</span>
              <span><i class="legend-dot legend-dot--unknown"></i>No data</span>
            </div>
            <div class="pagination-controls" aria-label="Availability timeline pages">
              <button class="button button--ghost" type="button" :disabled="availabilityLoading || availabilityCursorHistory.length === 0" @click="showPreviousAvailabilityPage">Earlier</button>
              <button class="button button--ghost" type="button" :disabled="availabilityLoading || availability.nextCursor === null" @click="showNextAvailabilityPage">Later</button>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section v-if="detailProbeId !== null" class="content-panel data-table-panel">
      <div class="content-panel__heading">
        <div>
          <p class="eyebrow">Detail view</p>
          <h2>{{ incidentLog?.displayName ?? "Internet incidents" }}</h2>
          <p v-if="incidentLog">
            {{ formatDateTime(incidentLog.from, userSettingsStore.settings.dateTimeFormat) }}
            –
            {{ formatDateTime(incidentLog.to, userSettingsStore.settings.dateTimeFormat) }}
          </p>
        </div>
        <button class="button button--ghost" type="button" @click="closeIncidentDetail">Close</button>
      </div>
      <p v-if="incidentLoadingError" class="form-error" role="alert">{{ incidentLoadingError }}</p>
      <p v-else-if="incidentsLoading" class="placeholder-panel">Loading Internet incidents…</p>
      <p v-else-if="incidentLog !== null && incidentLog.incidents.length === 0" class="placeholder-panel">
        No Internet interruptions in the selected bucket window.
      </p>
      <div v-else-if="incidentLog !== null" class="data-table data-table--incidents">
        <div class="data-table__header">
          <span>From</span>
          <span>To</span>
          <span>Duration</span>
          <span>Reason</span>
          <span>Classification</span>
          <span>Samples</span>
        </div>
        <div
          v-for="incident in incidentLog.incidents"
          :key="`${incident.startedAt}-${incident.endedAt}`"
          class="data-table__row"
        >
          <span>{{ formatDateTime(incident.startedAt, userSettingsStore.settings.dateTimeFormat) }}</span>
          <span>{{ formatDateTime(incident.endedAt, userSettingsStore.settings.dateTimeFormat) }}</span>
          <span>{{ formatDuration(incident.durationMs) }}</span>
          <span>{{ incidentStateLabel(incident.state) }}</span>
          <span>{{ incidentClassificationLabel(incident.classification) }}</span>
          <span>{{ incident.sampleCount }}</span>
        </div>
      </div>
    </section>
    <div
      v-if="bucketTooltipPanel.visible"
      ref="bucketTooltipElement"
      class="internet-availability-timeline__tooltip"
      role="tooltip"
      :style="{
        left: `${bucketTooltipPanel.left}px`,
        top: `${bucketTooltipPanel.top}px`,
        '--tooltip-arrow-left': `${bucketTooltipPanel.arrowLeft}px`,
      }"
    >
      {{ bucketTooltipPanel.text }}
    </div>
  </AppShell>
</template>
